import type {
  CreateWorkOrderInput,
  SetSlaInput,
  ClassifyInput,
  ReceiptInput,
  WorkSettings,
  ContractorAction,
  TransitionInput,
  WorkOrder,
  WorkOrderEvent,
  WorkOrderStatus,
  WorkOrderEvidence,
} from "../domain/workOrders";
import type { DaySummary, Movement, MovementEvent, MovementMessage, ScheduleLine } from "../domain/aodb";
import type { Checkpoint, FlowDay, LanePlan, Observation } from "../domain/flow";
import type { FeedDraft, FeedMessage, FeedOutcome, FeedResult, FeedTestResult, FlightFeed } from "../domain/flightFeeds";
import type {
  FinanceConnection,
  FinanceConnectionInput,
  FinanceImportResult,
  FinanceLatest,
  FinanceStatus,
  FinanceTest,
} from "../domain/sapConnection";
import type { AllocDay, EngineResult, Policy, Proposal, Refusal, Resource, Rule as AllocRule, WhatIf, Window } from "../domain/allocation";
import type { ActualKey, NewMovement } from "../domain/flightEntries";
import type {
  ContactOrg,
  ContactPerson,
  Directory,
  NotifyRule,
  Reach,
  Recipient,
  WorkOrderMessage,
} from "../domain/contacts";
import type {
  Call,
  CallDirectory,
  CallMedia,
  CallPoll,
  CallPolicy,
  CallMessage,
  CallSettingsPatch,
  CallStartOptions,
  DecisionRule,
  DndState,
  IceConfig,
  LiveCall,
  Meeting,
  MeetingDraft,
  ParticipantRole,
  PolicyView,
} from "../domain/calls";

/** The platform's answer to "may this person call that one". */
export interface CallCheck {
  allowed: boolean;
  video: boolean;
  reason: string;
  rule: DecisionRule;
}
import type { DispatchGroup, DispatchInput, DispatchNotification, DispatchResult } from "../domain/dispatch";
import type { PlatformStores, StoresApplied, StoresImported, StoresOp } from "../domain/storesOps";
import type { Alert, AlertExplanation, AlertKind } from "../domain/alerts";
import type { EstateSummary, NewNoteInput, SiteNote } from "../domain/estate";
import type { FleetAssetRow } from "../domain/fleet";
import type {
  ActionKind,
  AssetHistoryPoint,
  LogEntry,
  TestHistory,
  ConsumptionPoint,
  LatestTest,
  NewTestInput,
  TestingSummary,
  TestRecord,
  TestRecordDetail,
  TestType,
} from "../domain/testing";
import type { Inspection, InspectionDetail, InspectionStatus } from "../domain/inspections";
import type {
  InspectionRound,
  InspectionRoundInput,
  InspectionRoundQuery,
  InspectionRoundResult,
} from "../domain/inspectionRounds";
import type { MapFeature, MapLayer } from "../domain/geo";
import type { ComplianceReport, Rule, VerifyRuleInput, VerifyRuleResult } from "../domain/compliance";
import type {
  DocumentDetail,
  DocumentRow,
  DocumentStatus,
  DocumentType,
  NewDocumentInput,
  NewDocumentResult,
  NewRevisionInput,
  NewRevisionResult,
  RevisionTransitionInput,
  RevisionTransitionResult,
} from "../domain/library";
import type { AttachedFile, CertificateIssueInput, CertificateWrite, NewCertificateInput } from "../domain/certificates";
import type {
  AttentionReport,
  GateDecisionInput,
  GateDecisionResult,
  GateSubmitInput,
  GateSubmitResult,
  HoldPointReport,
  InspectionWriteResult,
  ItemResultInput,
  CloseActionInput,
  CreateProjectInput,
  NewLogEntryInput,
  ProjectLog,
  ScheduleInput,
  ScheduleResult,
  UpdateProjectInput,
  ProjectDetail,
  ProjectInspectionDetail,
  ProjectQuery,
  ProjectRow,
  ProjectVocabulary,
  RecordResultInput,
} from "../domain/projects";
import type { PlanAnswer } from "../domain/planning";
import type { LiabilityCurve, LiabilityRegister } from "../domain/liability";
import type {
  AmAssetDetail,
  AmCondition,
  AmFilter,
  AmImportResult,
  AmImportRow,
  AmLifecycle,
  AmMaintenance,
  AmPatch,
  AmPlan,
  AmPlanInput,
  AmPolicy,
  AmRegister,
  AmTypeDef,
  AmCreateInput,
  AmHistoryEvent,
  AmNote,
  NoteCategory,
} from "../domain/assetManagement";
import type {
  HrCost,
  HrEmployeeInput,
  HrEmployeePatch,
  HrLabour,
  HrPeople,
  HrRulesPatch,
  HrRulesView,
  HrSaudization,
} from "../domain/hr";
import type { HrAccessView } from "../domain/hrAccess";
import type {
  HrAttendanceDay,
  HrAttendanceInput,
  HrAttendanceRow,
  HrRecord,
  HrRecordInput,
  HrStaffFile,
  RecordStatus,
} from "../domain/hrFile";
import type {
  HrChannels,
  HrClockEvent,
  HrClockInput,
  HrGeofence,
  HrMe,
  HrNotification,
  HrSelfRequest,
} from "../domain/hrClock";
import type { ImportResponse } from "../domain/hrImport";
import type { ClosureCost, CriticalReport } from "../domain/capacity";
import type { DriftCheck, DriftSchedule } from "../domain/drift";
import type { Fit, Population } from "../domain/reliability";
import type { Divergence, FactSummary } from "../domain/ledger";
import type { Difference, MigrationStatus, ScreenDetail, ScreenMode, Triage } from "../domain/migration";
import type {
  AddedPerson,
  AuthStatus,
  Invitation,
  Invited,
  NewPerson,
  Person,
  ProviderChange,
  RoleRow,
  SetupState,
} from "../domain/administration";
import type { AccessLevel } from "../assistant/access";
import type { CanWork } from "../domain/weather";
import type { Programme } from "../domain/programme";
import type { MeterRegister, MeterUploadResult, NewMeterInput } from "../domain/meters";
import type { Study } from "../domain/counterfactual";
import type { Chain } from "../domain/provenance";
import type { Kpi } from "../domain/kpis";
import type {
  AmConditionInput,
  AmConditionResult,
  EnvelopeInput,
  FundingEnvelope,
  KpiTargetChange,
  KpiTargetInput,
  ThresholdsInput,
} from "../domain/entryForms";
import type { AssistQuestion, AssistReply } from "../domain/assist";
import type { Site } from "../domain/sites";
import type {
  AssessedHazard,
  AssessHazardInput,
  CloseHazardInput,
  FiledReport,
  FileReportInput,
  HazardActionInput,
  HazardDetail,
  HazardPage,
  IncidentCategory,
  IncidentStatus,
  RaisedIncident,
  RaiseHazardInput,
  RaiseIncidentInput,
  ReportStatus,
  SafetyReport,
  SafetyReportPage,
  SafetyVocabulary,
  SecurityIncidentPage,
} from "../domain/safety";

/**
 * Everything a screen may ask for. Screens depend on this contract only —
 * never on fetch, never on the mock data — so swapping the adapter changes
 * nothing above it.
 *
 * Each method maps to one published platform endpoint (router and function
 * named beside it). A screen that needs something not listed here needs an
 * endpoint first, which is the API-completeness rule the console established.
 */
import type {
  Closure,
  DeclareClosureInput,
  DeclaredClosure,
  EndedClosure,
  ListClosuresOptions,
} from "../domain/closures";

export interface PlatformEvent {
  sequence: number;
  type: string;
  subjectKind: string;
  subjectId: string;
}

export interface EventPage {
  events: PlatformEvent[];
  /** the sequence of the last event returned, or `after` when there were none */
  cursor: number;
  more: boolean;
}

/** What a mode change did, and where the guard refused, why. */
export interface ModeChange {
  from: ScreenMode;
  to: ScreenMode;
  forced: boolean;
  /** present only when the guard refused: nothing moved */
  blocking?: string[];
}

/** One crew, where their device last said they were. */
export interface FieldPosition {
  principalId?: string;
  principal?: string;
  /** the crew the device says it is with, where the deployment names teams */
  team?: string;
  event: "fix" | "arrived" | "left" | "closed";
  workOrderId?: string;
  /** the work order's reference, resolved by the platform */
  workOrder?: string;
  lon: number;
  lat: number;
  /** the device's own estimate, in metres; kept so a reader can discount it */
  accuracyM?: number;
  headingDeg?: number;
  speedMps?: number;
  source: "device" | "last" | "manual";
  at: string;
  /** seconds since the fix, computed by the platform against its own clock */
  ageS: number;
  stale: boolean;
  /** true when the device's clock is ahead of the platform's, which makes a fix untrustworthy */
  clockAhead?: boolean;
}

export interface DataSource {
  readonly kind: "platform" | "mock";

  /** GET /v1/console/bootstrap — console.py `bootstrap`. Requires site:read. */
  bootstrap(): Promise<Bootstrap>;

  /** GET /v1/assets/summary?site_id&asset_type… — registry.py `assets_summary`: one slice of the estate. */
  getEstateSummary(siteId: string, assetTypes: string[]): Promise<EstateSummary>;

  /**
   * GET /v1/assets?site_id&asset_type=a,b,c — registry.py `list_assets`.
   *
   * Individual assets with their pack-declared attributes, which the summary
   * above deliberately does not carry. The equipment register needs the
   * attributes themselves — a licence expiry, an airside permit, the driver
   * assigned — and those are per vehicle, not per type.
   *
   * Returns an empty list on a deployment whose installed pack declares none
   * of these types, which is how the register knows to fall back to its
   * entered copy rather than showing an empty table.
   */
  listAssetsOfTypes(siteId: string, assetTypes: readonly string[]): Promise<FleetAssetRow[]>;

  /** GET /v1/notes?site_id&topic — registry.py `list_notes`: append-only notes, newest first. */
  listNotes(siteId: string, topic: string): Promise<SiteNote[]>;

  /** POST /v1/notes — registry.py `add_note`. Requires note:write; an asset tag must exist at the site. */
  addNote(siteId: string, input: NewNoteInput): Promise<void>;

  /** GET /v1/test-types — testing.py: what this tenant tests, how often, and what each result is judged against. */
  listTestTypes(): Promise<TestType[]>;

  /** GET /v1/test-records?site_id — every test recorded at the site, newest first. */
  listTestRecords(siteId: string): Promise<TestRecord[]>;

  /** GET /v1/test-records/summary?site_id — per type: the last test, its verdict, and whether the next is late. */
  getTestingSummary(siteId: string): Promise<TestingSummary>;

  /** GET /v1/test-records/{id} — the record, its results, its files and its comparison with the test before. */
  getTestRecord(id: string): Promise<TestRecordDetail>;

  /** GET /v1/test-records/latest?site_id&test_type — the newest test of a type and its comparison. */
  getLatestTest(siteId: string, testType: string): Promise<LatestTest>;

  /** POST /v1/test-records — judged on arrival, never edited afterwards. Requires test:write. */
  createTestRecord(siteId: string, input: NewTestInput): Promise<{ id: string; reference: string }>;

  /** POST /v1/test-records/{id}/revise — the corrected record as its next revision, with the reason. 409 if already revised. */
  reviseTestRecord(
    id: string,
    input: NewTestInput & { reason: string },
  ): Promise<{ id: string; reference: string; revision: number }>;

  /** GET /v1/test-records/{id}/history — every revision, the actions taken, and the log of every change. */
  getTestHistory(id: string): Promise<TestHistory>;

  /** POST /v1/test-records/{id}/actions — what was done about a test. Requires test:write. */
  addTestAction(
    id: string,
    input: { kind: ActionKind; body: string; workOrderId?: string; assetTags?: string[] },
  ): Promise<void>;

  /** GET /v1/test-records/activity?site_id — the site's testing log. */
  getTestingActivity(siteId: string): Promise<LogEntry[]>;

  /** GET /v1/test-records/asset-history — one asset's result in every current test of a type. */
  getAssetTestHistory(siteId: string, testType: string, assetTag: string): Promise<AssetHistoryPoint[]>;

  /** POST /v1/evidence (subject test_record) — a test report or data file. Requires evidence:write. */
  uploadTestFile(recordId: string, file: File, note?: string): Promise<void>;

  /** GET /v1/evidence/{object}/content — the file itself. */
  downloadFile(objectId: string): Promise<Blob>;

  /** GET /v1/readings/consumption?site_id&asset_type…&bucket&days — consumption per meter per period. */
  getConsumption(
    siteId: string,
    assetTypes: string[],
    bucket: "day" | "week" | "month",
    days: number,
  ): Promise<ConsumptionPoint[]>;

  /** GET /v1/counters?site_id&asset_type… — the meter register: every meter, its feed, its last reading. */
  listMeters(siteId: string, assetTypes: string[]): Promise<MeterRegister>;

  /** POST /v1/counters — connect a meter's feed (asset:write). */
  registerMeter(siteId: string, input: NewMeterInput): Promise<void>;

  /** POST /v1/counters/upload — a BMS export, through the same connector as a live feed (reading:write). */
  uploadMeterReadings(siteId: string, csv: string, filename?: string): Promise<MeterUploadResult>;

  /** GET /v1/me/preferences/{key} — me.py: the signed-in person's own setting, or null when unset. */
  getPreference(key: string): Promise<unknown>;

  /** PUT /v1/me/preferences/{key} — me.py. 403 when the request names no person. */
  setPreference(key: string, value: unknown): Promise<void>;

  /** DELETE /v1/me/preferences/{key} — me.py: back to the platform's default. */
  deletePreference(key: string): Promise<void>;

  /**
   * GET /v1/events — integrate.py: what changed on the platform, after a
   * sequence already seen. The sequence is gap-free, so `after` is a safe
   * cursor; `more` says a page was full. What the screens do with it is
   * refresh whatever they are showing — see `LiveRefresh`.
   */
  pollEvents(siteId: string, after: number, limit?: number): Promise<EventPage>;

  /**
   * GET /v1/closures — compliance.py: surfaces taken out of use, newest first,
   * since a date when given. Closures that have not started are left out
   * unless `includePlanned` asks for them.
   */
  listClosures(siteId: string, since?: string, opts?: ListClosuresOptions): Promise<Closure[]>;

  /** GET /v1/evidence — the photographs and files held against a record, by its kind and id. */
  listEvidence(subjectKind: string, subjectId: string): Promise<WorkOrderEvidence[]>;

  /** GET /v1/work-orders?site_id&status — ops.py `list_work_orders`. Capped at 1000; `truncated` is reported. */
  listWorkOrders(query: WorkOrderQuery): Promise<WorkOrderPage>;

  /** GET /v1/work-orders/{id} — work.py `get_work_order`, with tasks, defects, evidence and allowed_transitions. */
  getWorkOrder(id: string): Promise<WorkOrder>;

  /** GET /v1/work-orders/{id}/history — work.py `work_order_history`. */
  getWorkOrderHistory(id: string): Promise<WorkOrderEvent[]>;

  /** POST /v1/work-orders — work.py `create_work_order`. Returns the created order's detail. */
  createWorkOrder(input: CreateWorkOrderInput): Promise<WorkOrder>;

  /**
   * POST /v1/work-orders/{id}/transition — work.py `transition_work_order`. 409 names the refusal.
   * The same `idempotencyKey` sent again answers with the first move rather than refusing it.
   */
  transitionWorkOrder(id: string, input: TransitionInput, idempotencyKey?: string): Promise<WorkOrder>;

  /** POST /v1/dispatch — dispatch.py: a P1 work order for a team, dispatched, and a message to every member. */
  dispatchTeam(input: DispatchInput): Promise<DispatchResult>;

  /** GET /v1/dispatch/groups?site_id — the teams and who is in each. */
  listDispatchGroups(siteId: string): Promise<DispatchGroup[]>;

  /** PUT /v1/dispatch/groups/{key} — who is in a team and how to reach them. */
  saveDispatchGroup(siteId: string, group: DispatchGroup): Promise<DispatchGroup>;

  /** GET /v1/dispatch/notifications?work_order_id — what was sent to whom, and how it went. */
  listDispatchNotifications(workOrderId: string): Promise<DispatchNotification[]>;

  /**
   * GET /v1/stores/register?site_id — stores.py: the whole stores register in
   * the web model's shape, and whether the platform holds one for this site
   * yet or it is still kept as a note.
   */
  storesRegister(siteId: string): Promise<PlatformStores>;

  /**
   * POST /v1/stores/batch — the steps an operation reported (storesOps.ts),
   * in one transaction, all or none. Sent with an Idempotency-Key, so a retry
   * is answered rather than repeated. 400/404/409 carry the refusal verbatim.
   */
  storesApply(siteId: string, ops: StoresOp[]): Promise<StoresApplied>;

  /** POST /v1/stores/import-note — the one-time move of the site's `estate.stores` note; asked again, it answers the same. */
  storesImportNote(siteId: string): Promise<StoresImported>;

  /** GET /v1/aodb/day — the flight record for one service date, with predictions and flags. */
  aodbDay(siteId: string, date?: string): Promise<{ serviceDate: string; covered: boolean; items: Movement[] }>;

  /** GET /v1/aodb/summary — on time, delay by code, predicted late, flagged. */
  aodbSummary(siteId: string, date?: string): Promise<DaySummary>;

  /** GET /v1/aodb/movements/{id} — one movement, its full history and its messages. */
  aodbMovement(id: string): Promise<{ movement: Movement; history: MovementEvent[]; messages: MovementMessage[] }>;

  /** PATCH /v1/aodb/movements/{id} — change it; refused (409) if the record moved on since `version`. */
  aodbChange(
    id: string,
    changes: Partial<Movement>,
    version?: number,
    note?: string,
  ): Promise<{ movement: Movement; changed: string[] }>;

  /**
   * POST /v1/aodb/movements/{id}/notices — someone told us the flight will be
   * late: the estimate moves, the code goes first, the notice is kept. Refused
   * for a flight that has flown or is cancelled; 409 on a stale `version`.
   */
  aodbNotice(
    siteId: string,
    movementId: string,
    notice: { source: string; newTime: string; code?: string; note?: string },
    version?: number,
  ): Promise<{ movement: Movement; changed: string[] }>;

  /* ── build 6: the flight-record figures a person enters by hand ── */

  /**
   * POST /v1/aodb/movements — a movement the seasonal schedule does not have
   * (a charter, a diversion, an extra section). Refused in words: a bad
   * airline code or number, a time not on its day, a day long closed. 409
   * when that flight is already on the record for that day.
   */
  aodbAdd(siteId: string, draft: NewMovement): Promise<Movement>;

  /**
   * POST /v1/aodb/movements/{id}/actuals — off-block and take-off on a
   * departure, landing and on-block on an arrival, entered by a person.
   * Refused: a time before the flight's day, in the future, out of order, or
   * on the wrong direction. `replaced` names what a message had set.
   */
  aodbActuals(
    id: string,
    times: Partial<Record<ActualKey, string>>,
    version?: number,
    note?: string,
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }>;

  /**
   * POST /v1/aodb/movements/{id}/loads — booked, on board, transfer, bags,
   * cargo and mail; seats only when `capacity` is passed (null clears them).
   * Refused: below zero, not whole, more on board than seats.
   */
  aodbLoads(
    id: string,
    loads: Partial<Record<string, number | null>>,
    opts?: { capacity?: number | null; version?: number; note?: string },
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }>;

  /**
   * PUT /v1/aodb/movements/{id}/delay-codes — the IATA delay codes and their
   * minutes, the whole list. Refused: a code IATA does not have, minutes
   * adding up to more than the flight was late.
   */
  aodbDelayCodes(
    id: string,
    codes: { code: string; minutes?: number }[],
    version?: number,
    note?: string,
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }>;

  /**
   * PUT /v1/aodb/movements/{id}/tobt — the handler's committed off-block
   * time. Only on a departure not yet off blocks, and not already passed.
   */
  aodbTobt(
    id: string,
    tobt: string,
    opts?: { givenBy?: string; version?: number; note?: string },
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }>;

  /** GET /v1/aodb/schedule — the seasonal lines. */
  aodbSchedule(siteId: string): Promise<ScheduleLine[]>;

  /** POST /v1/aodb/schedule/import — an SSIM file. */
  aodbImportSsim(
    siteId: string,
    ssim: string,
    season?: string,
  ): Promise<{ legs: number; lines: number; skipped: number }>;

  /** POST /v1/aodb/generate — the days' movements from the schedule, once each. */
  aodbGenerate(
    siteId: string,
    from: string,
    to: string,
  ): Promise<{ created: number; alreadyThere: number; lines: number }>;

  /** POST /v1/aodb/messages — an MVT or a structured update, applied or held. */
  aodbPostMessage(
    siteId: string,
    kind: "mvt" | "aidx",
    raw: string,
  ): Promise<{ outcome: MovementMessage["outcome"]; refusal?: string; changed: string[] }>;

  /** GET /v1/aodb/messages — the messages in, and what they did. */
  aodbMessages(siteId: string): Promise<MovementMessage[]>;

  /** POST /v1/aodb/predict — predict and check a day now. */
  aodbPredict(siteId: string, date?: string): Promise<{ predicted: number; moved: number; flagged: number }>;

  /** GET /v1/aodb/feeds — every flight data feed (AIDX, Type B, JSON) with its status; `sample` on the mock. */
  flightFeeds(siteId: string): Promise<{ items: FlightFeed[]; sample: boolean }>;

  /** GET /v1/aodb/feeds/messages — the messages in, raw, with their outcome and reason. */
  flightFeedMessages(siteId: string, opts?: { feedId?: string; outcome?: FeedOutcome }): Promise<FeedMessage[]>;

  /** POST /v1/aodb/feeds (no id) or PATCH /v1/aodb/feeds/{id} — configure a feed. Secrets are named, never sent. */
  saveFlightFeed(siteId: string, draft: FeedDraft, id?: string): Promise<FlightFeed>;

  /** POST /v1/aodb/feeds/{id}/test — a sample message, or one fetch, processed and rolled back. */
  testFlightFeed(id: string, sample?: string): Promise<FeedTestResult>;

  /** POST /v1/aodb/feeds/messages/{id}/replay — process a kept message again. */
  replayFeedMessage(id: string): Promise<{ results: FeedResult[] }>;

  /** GET /v1/allocation/day — the plan for stands, gates, counters and belts, judged against the flight record now. */
  allocDay(siteId: string): Promise<AllocDay>;

  /** POST /v1/allocation/replan — a proposal now: fix what is broken, or optimise the whole day. */
  allocReplan(siteId: string, mode: "replan" | "plan"): Promise<Proposal>;

  /** POST /v1/allocation/proposals/{id} — apply or reject a proposal. */
  allocDecide(siteId: string, proposalId: string, decision: "apply" | "reject", note?: string): Promise<{ applied: number }>;

  /** POST /v1/allocation/assign — a person's own placement; refused with every reason unless forced. */
  allocAssign(
    siteId: string,
    key: string,
    codes: string[],
    opts?: { note?: string; force?: boolean },
  ): Promise<{ refused: Refusal[]; learned?: string }>;

  /** POST /v1/allocation/release — take a flight off its place; logged. 404 when nothing is placed. */
  allocRelease(siteId: string, key: string, note?: string): Promise<{ released: string }>;

  /** POST /v1/allocation/resources — add one stand, gate, counter or belt; 409 if the code is taken. */
  allocAddResource(siteId: string, item: Resource): Promise<{ saved: string }>;

  /** GET /v1/allocation/resources?all=true — the whole register, resources taken off it included (active false). */
  allocRegister(siteId: string): Promise<(Resource & { active: boolean })[]>;

  /**
   * PATCH /v1/allocation/resources/{code} — change one resource: name, group,
   * order, largest size, contact stand, its gate, uses, neighbors, bus gate,
   * on or off the register. Refused, in words, when a flight placed on it
   * would no longer fit, or the change names a gate the register lacks.
   */
  allocChangeResource(
    siteId: string,
    code: string,
    changes: Partial<Resource> & { active?: boolean },
    note?: string,
  ): Promise<{ resource: Resource & { active: boolean }; changed: string[] }>;

  /** POST /v1/allocation/what-if — the re-plan a closure or a delay would force; nothing is saved. */
  allocWhatIf(siteId: string, change: WhatIf): Promise<EngineResult>;

  /** GET /v1/allocation/windows — the cheapest times to close a resource for maintenance. */
  allocWindows(siteId: string, code: string, minutes: number, hours?: number): Promise<Window[]>;

  /** PUT /v1/allocation/policies/{kind} — automation level, buffer, freeze, risk threshold. */
  allocSetPolicy(siteId: string, policy: Policy): Promise<Policy[]>;

  /** PUT /v1/allocation/rules — add or change a rule. */
  allocSaveRule(siteId: string, rule: AllocRule): Promise<AllocRule[]>;

  /** GET /v1/flow/day — every queue forecast, the recommended lanes, the flights at risk. */
  flowDay(siteId: string): Promise<FlowDay>;

  /** POST /v1/flow/observations — a queue counted or a wait timed; it resets the model at that moment. */
  flowObserve(siteId: string, obs: Omit<Observation, "source" | "by">): Promise<{ ok: true }>;

  /** PUT /v1/flow/plans/{code} — adopt a lane plan. */
  flowAdopt(siteId: string, code: string, steps: LanePlan["steps"], note?: string): Promise<LanePlan[]>;

  /** PUT /v1/flow/checkpoints — add or change a checkpoint. */
  flowSaveCheckpoint(siteId: string, cp: Checkpoint): Promise<Checkpoint[]>;

  /** GET /v1/finance/connection — the site's SAP connection; the secret only as set / not set. null: none yet. */
  financeConnection(siteId: string): Promise<FinanceConnection | null>;

  /** PUT /v1/finance/connection — create or change it. The secret is write-only. */
  saveFinanceConnection(siteId: string, input: FinanceConnectionInput): Promise<FinanceConnection>;

  /** POST /v1/finance/connection/test — reach SAP, sign in, read one journal line. */
  testFinanceConnection(siteId: string): Promise<FinanceTest>;

  /** POST /v1/finance/read-now — read a period now; the run reports itself on financeStatus. */
  readFinanceNow(
    siteId: string,
    period?: { fiscalYear?: string; fiscalPeriod?: string },
  ): Promise<{ id: string; status: string; fiscalYear: string; fiscalPeriod: string }>;

  /** GET /v1/finance/status — configured or not, the last read and its report, running or not. */
  financeStatus(siteId: string): Promise<FinanceStatus>;

  /** GET /v1/finance/read — the latest journal read, live or from a journal file. */
  financeLatest(siteId: string): Promise<FinanceLatest>;

  /** POST /v1/finance/import — a journal extract or the monthly pack, into the same record as live reads. */
  importFinanceFile(siteId: string, fileName: string, text: string): Promise<FinanceImportResult>;

  /** DELETE /v1/allocation/rules/{id} */
  allocDeleteRule(siteId: string, id: string): Promise<AllocRule[]>;

  /** GET /v1/contacts — the directory: organisations, their people, every way to reach each. */
  getDirectory(siteId: string): Promise<Directory>;

  /** POST or PUT /v1/contacts/orgs — add or change an organisation (retire it with active false). */
  saveContactOrg(siteId: string, org: Omit<ContactOrg, "people">): Promise<{ id: string }>;

  /** POST or PUT /v1/contacts/people — add or change a person; their numbers and addresses are replaced. */
  saveContactPerson(person: ContactPerson): Promise<{ id: string }>;

  /** GET /v1/notify-rules — who is told about which work. */
  listNotifyRules(siteId: string): Promise<NotifyRule[]>;

  /** POST or PUT /v1/notify-rules — keep a rule. */
  saveNotifyRule(rule: NotifyRule): Promise<NotifyRule>;

  /** POST /v1/notify-rules/preview — who a set of recipients reaches, and where. */
  previewRecipients(siteId: string, recipients: Recipient[]): Promise<Reach[]>;

  /* ── staff calls (routers/calls.py) ─────────────────────────────────── */

  /** GET /v1/calls/ice — STUN and TURN servers for this person, and whether a relay is configured. */
  callsIce(): Promise<IceConfig>;

  /** GET /v1/calls/directory — everyone on staff calls but me, with what the policy says about calling each. */
  callsDirectory(q?: string): Promise<CallDirectory>;

  /** GET /v1/calls/poll — rings, my call, signals after `after`, chat after `afterMsg`; also marks me present. */
  callsPoll(after: number, afterMsg?: number): Promise<CallPoll>;

  /* ── build 2: safety ── */
  /* The safety register (routers/safety.py), the security incident register
     (routers/security.py) and nothing else: the old safety boards read and
     write through these. Reading the register, filing a report and reading
     reports are separate permissions on purpose — see PERMISSION. */

  /** GET /v1/safety/vocabulary — the pack's risk matrix and hazard categories. Requires safety:read. */
  safetyVocabulary(): Promise<SafetyVocabulary>;

  /** GET /v1/safety/hazards?site_id — the register, each hazard with its risk as a state. Requires safety:read. */
  listHazards(siteId: string): Promise<HazardPage>;

  /** GET /v1/safety/hazards/{id} — one hazard with its assessments, actions, reports and acceptances. */
  getHazard(id: string): Promise<HazardDetail>;

  /** POST /v1/safety/hazards — enter a condition; the key makes a second press the same hazard. Requires safety:write. */
  raiseHazard(input: RaiseHazardInput, key?: string): Promise<{ id: string; reference: string }>;

  /** POST /v1/safety/hazards/{id}/assessments — score it against the pack's matrix. Requires safety:write. */
  assessHazard(id: string, input: AssessHazardInput): Promise<AssessedHazard>;

  /** POST /v1/safety/hazards/{id}/actions — a control promised against it. Requires safety:write. */
  addHazardAction(id: string, input: HazardActionInput): Promise<{ seq: number; status: string; warnings: string[] }>;

  /** POST /v1/safety/hazards/{id}/actions/{seq}/complete — done, or dropped. Requires safety:write. */
  completeHazardAction(
    id: string,
    seq: number,
    status: "done" | "cancelled",
  ): Promise<{ hazardStatus: string; warnings: string[] }>;

  /** POST /v1/safety/hazards/{id}/accept-risk — carry the current risk, with a reason. Requires safety:close. */
  acceptHazardRisk(id: string, input: { rationale: string; reviewDue?: string }): Promise<{ note: string }>;

  /** POST /v1/safety/hazards/{id}/close — closed or rejected; intolerable needs an acceptance. Requires safety:close. */
  closeHazard(id: string, input: CloseHazardInput): Promise<{ status: string; warnings: string[] }>;

  /** GET /v1/safety/reports?site_id — protected reports come without the account or the place. Requires safety:report_read. */
  listSafetyReports(siteId: string): Promise<SafetyReportPage>;

  /** GET /v1/safety/reports/{id} — the account, never the protected reporter. Requires safety:report_read. */
  getSafetyReport(id: string): Promise<SafetyReport>;

  /** POST /v1/safety/reports — the platform stamps who and when; the key makes a second press the same report. Requires safety:report. */
  fileSafetyReport(input: FileReportInput, key?: string): Promise<FiledReport>;

  /** POST /v1/safety/reports/{id}/status — closed and rejected need an outcome. Requires safety:write. */
  setSafetyReportStatus(
    id: string,
    input: { status: ReportStatus; outcome?: string },
  ): Promise<{ reference: string; status: ReportStatus }>;

  /** POST /v1/safety/reports/{id}/link — tie it to a live hazard, or untie it with null. Requires safety:write. */
  linkSafetyReport(id: string, hazardId: string | null): Promise<{ reference: string; hazardReference: string | null }>;

  /** POST /v1/safety/reports/{id}/hazard — raise a hazard from it; nothing of the account is copied. Requires safety:write. */
  raiseHazardFromReport(
    id: string,
    input: { category: string; title: string; description?: string },
  ): Promise<{ id: string; reference: string; warning?: string }>;

  /** GET /v1/security/vocabulary — the pack's incident categories. Requires security:read. */
  securityVocabulary(): Promise<IncidentCategory[]>;

  /** GET /v1/security/incidents?site_id — restricted ones are absent without clearance, and it says so. Requires security:read. */
  listSecurityIncidents(siteId: string): Promise<SecurityIncidentPage>;

  /** POST /v1/security/incidents — severity and classification default from the pack. Requires security:incident_write. */
  raiseSecurityIncident(input: RaiseIncidentInput, key?: string): Promise<RaisedIncident>;

  /** POST /v1/security/incidents/{id}/status — the pack's transitions; a close needs an outcome. */
  setSecurityIncidentStatus(
    id: string,
    input: { status: IncidentStatus; outcome?: string; note?: string },
  ): Promise<{ status: IncidentStatus; warnings: string[] }>;

  /** POST /v1/security/incidents/{id}/links — evidence such as the work order raised from it. */
  linkSecurityIncident(id: string, input: { subjectKind: string; subjectId: string; note?: string }): Promise<void>;

  /** POST /v1/calls — ring one person or several; v2 adds a title, who can join, and urgent. */
  callsCreate(
    invitees: string[],
    media: CallMedia,
    opts?: CallStartOptions,
  ): Promise<{ call: Call; notes: string[] }>;

  /** POST /v1/calls/{id}/answer */
  callsAnswer(callId: string, video: boolean): Promise<Call>;

  /** POST /v1/calls/{id}/decline */
  callsDecline(callId: string): Promise<Call>;

  /** POST /v1/calls/{id}/leave */
  callsLeave(callId: string): Promise<Call>;

  /** POST /v1/calls/{id}/invite — add people to the call. */
  callsInvite(callId: string, invitees: string[]): Promise<Call>;

  /** POST /v1/calls/{id}/signal — an offer, an answer or an ICE candidate for one other person. */
  callsSignal(callId: string, to: string, kind: "offer" | "answer" | "ice", payload: unknown): Promise<void>;

  /** POST /v1/calls/{id}/media — my microphone and camera, on or off; v2 adds whether I share my screen. */
  callsMedia(callId: string, audioOn: boolean, videoOn: boolean, sharing?: boolean): Promise<Call>;

  /* ── staff calls v2: do not disturb, hosts, lobby, chat, meetings ──── */

  /** GET /v1/calls/me/dnd */
  callsDnd(): Promise<DndState>;

  /** PUT /v1/calls/me/dnd — `until` an ISO time, "forever", or null for off. */
  callsSetDnd(until: string | "forever" | null, allow: string[]): Promise<DndState>;

  /** POST /v1/calls/{id}/join — straight in, or into the lobby. */
  callsJoin(callId: string): Promise<{ call: Call; state: "joined" | "waiting" }>;

  /** POST /v1/calls/{id}/settings — who can join, locked, title (host or co-host). */
  callsSettings(callId: string, patch: CallSettingsPatch): Promise<Call>;

  /** POST /v1/calls/{id}/admit — let somebody in from the lobby, or turn them away. */
  callsAdmit(callId: string, principalId: string, allow: boolean): Promise<Call>;

  /** POST /v1/calls/{id}/remove */
  callsRemove(callId: string, principalId: string): Promise<Call | undefined>;

  /** POST /v1/calls/{id}/mute — one person, or everybody ("all"). */
  callsMute(callId: string, who: string | "all"): Promise<Call | undefined>;

  /** POST /v1/calls/{id}/role — co-host, member, or hand the host over. */
  callsRole(callId: string, principalId: string, role: ParticipantRole): Promise<Call | undefined>;

  /** POST /v1/calls/{id}/end — end the call for everybody (host or co-host). */
  callsEnd(callId: string): Promise<Call | undefined>;

  /** POST /v1/calls/{id}/hand */
  callsHand(callId: string, up: boolean): Promise<Call>;

  /** GET /v1/calls/{id}/messages */
  callsMessages(callId: string, after?: number): Promise<CallMessage[]>;

  /** POST /v1/calls/{id}/messages */
  callsSendMessage(callId: string, text: string): Promise<CallMessage>;

  /** GET /v1/calls/live — group calls going on that I could come into. */
  callsLive(): Promise<LiveCall[]>;

  /** GET /v1/calls/meetings */
  callsMeetings(scope: "upcoming" | "past"): Promise<Meeting[]>;

  /** POST /v1/calls/meetings, or PUT /v1/calls/meetings/{id} with an id. */
  callsSaveMeeting(draft: MeetingDraft, id?: string): Promise<Meeting>;

  /** POST /v1/calls/meetings/{id}/cancel */
  callsCancelMeeting(id: string): Promise<Meeting>;

  /** POST /v1/calls/meetings/{id}/start — starts its call, or joins it when live. */
  callsStartMeeting(id: string): Promise<{ meeting: Meeting; call: Call; notes?: string[] }>;

  /** GET /v1/calls/history — calls I took part in, newest first. */
  callsHistory(limit?: number): Promise<Call[]>;

  /** GET /v1/calls/photo/{employee_id} — the face on a call, as an object URL; undefined when there is none. */
  callsPhoto(employeeId: string): Promise<string | undefined>;

  /** GET /v1/calls/policy — who may call whom. */
  callsPolicy(): Promise<PolicyView>;

  /**
   * PUT /v1/calls/policy — needs calls:policy. `basedOn` is the `updatedAt`
   * the editor read ("default" for the built-in one); a save from an older
   * reading is a 409 naming who saved since (build 5).
   */
  saveCallsPolicy(policy: CallPolicy, basedOn?: string): Promise<PolicyView>;

  /** POST /v1/calls/policy/check — may one person call another, and why. */
  checkCallsPolicy(from: string, to: string): Promise<CallCheck>;

  /** The sample only: a sample person rings me, so the ring card can be seen. */
  simulateIncomingCall?(media: CallMedia): Promise<void>;

  /** GET /v1/work-orders/{id}/messages — everything sent about one work order. */
  listWorkOrderMessages(workOrderId: string): Promise<WorkOrderMessage[]>;

  /** POST /v1/work-orders/{id}/sla — work.py `set_work_order_sla`. Needs a reason; 409 once the work is completed. */
  setWorkOrderSla(id: string, input: SetSlaInput): Promise<WorkOrder>;

  /** POST /v1/work-orders/{id}/classify — work.py `classify_work_order`: area, trade, contractor. */
  classifyWorkOrder(id: string, input: ClassifyInput): Promise<WorkOrder>;

  /** POST /v1/work-orders/{id}/receipt — the office records that the contractor has the order. */
  confirmWorkOrderReceipt(id: string, input: ReceiptInput): Promise<WorkOrder>;

  /** POST /v1/work-orders/{id}/notes — a note kept in the history with who wrote it and when. */
  addWorkOrderNote(id: string, text: string): Promise<void>;

  /** POST /v1/evidence — a photograph on the order; an "after" one on a completed order verifies it. */
  uploadWorkOrderPhoto(id: string, file: File, phase: "before" | "after"): Promise<WorkOrder>;

  /** GET / PUT /v1/work-orders/settings?site_id — whether an after photo verifies a completed order. */
  getWorkSettings(siteId: string): Promise<WorkSettings>;
  setWorkSettings(siteId: string, settings: WorkSettings): Promise<WorkSettings>;

  /**
   * The sample only: what the contractor's job link does on the platform (received, start, done with
   * a photo). On the platform the contractor does this from their phone; nothing in the console can.
   */
  simulateContractor?(id: string, action: ContractorAction, photo?: File, note?: string): Promise<WorkOrder>;

  /** GET /v1/insight/kpis?site_id — insight.py `kpis`: metrics and the uncomputable ones. */
  listKpis(siteId: string): Promise<Kpi[]>;

  /* ── build 6B: entry forms ── */
  /** POST /v1/insight/kpis/target — kpi:target. Set, change or agree this site's own target, with its basis. */
  setKpiTarget(siteId: string, input: KpiTargetInput): Promise<void>;
  /** POST /v1/insight/kpis/target/retire — kpi:target. 404 when the site has no target of its own. */
  retireKpiTarget(siteId: string, metricKey: string, reason: string): Promise<void>;
  /** GET /v1/insight/kpis/target/history — who set, agreed or retired a target, newest first. */
  kpiTargetHistory(siteId: string, metricKey: string): Promise<KpiTargetChange[]>;
  /** POST /v1/asset-management/assets/{id}/condition — asset:write. A surveyed score; health follows. */
  amRecordCondition(assetId: string, input: AmConditionInput): Promise<AmConditionResult>;
  /** GET /v1/liability/envelopes — insight:read. The site's own and the tenant-wide envelopes. */
  listEnvelopes(siteId: string): Promise<FundingEnvelope[]>;
  /** POST /v1/liability/envelopes — insight:write. One per year and kind; setting it again replaces it. */
  setEnvelope(siteId: string, input: EnvelopeInput): Promise<void>;
  /** DELETE /v1/liability/envelopes/{id} — insight:write. */
  removeEnvelope(id: string): Promise<void>;
  /** PUT /v1/test-types/{key}/thresholds — rule:verify. Records already judged keep their own copy. */
  setTestThresholds(siteId: string, key: string, input: ThresholdsInput): Promise<void>;

  /** GET /v1/alerts?site_id&kind — ops.py `list_alerts`. Open alerts only. */
  listAlerts(siteId: string, kind?: AlertKind): Promise<Alert[]>;

  /** GET /v1/insight/alerts/{id}/explain — insight.py `explain_alert`. */
  explainAlert(id: string): Promise<AlertExplanation>;

  /** GET /v1/inspections?site_id&status&template — work.py `list_inspections`. Requires inspection:read. */
  listInspections(query: InspectionQuery): Promise<Inspection[]>;

  /** GET /v1/inspections/{id} — work.py `get_inspection`, with its observations. */
  getInspection(id: string): Promise<InspectionDetail>;

  /** GET /v1/geo/layers?site_id — geo.py `list_layers`. Requires geo:read. */
  listMapLayers(siteId: string): Promise<MapLayer[]>;

  /** GET /v1/geo/features?site_id&layer&limit — geo.py `list_features`, a GeoJSON FeatureCollection. */
  listMapFeatures(siteId: string, layer: string): Promise<MapFeature[]>;

  /** GET /v1/insight/compliance?site_id — insight.py `compliance`: readiness by framework and obligations. */
  getCompliance(siteId: string): Promise<ComplianceReport>;

  /** GET /v1/insight/rules — insight.py `rule_status`: live and gated rules for this tenant. */
  listRules(): Promise<Rule[]>;

  /** POST /v1/insight/rules/verify — insight.py `verify_rule`. Requires rule:verify and a stated basis. */
  verifyRule(input: VerifyRuleInput): Promise<VerifyRuleResult>;

  /** GET /v1/library/documents?site_id&type&status&q — library.py `list_documents`. `site_id` means "applies at". */
  listDocuments(query: DocumentQuery): Promise<DocumentRow[]>;

  /** GET /v1/library/documents/{id} — library.py `get_document`, with revisions, links and acknowledgements. */
  getDocument(id: string): Promise<DocumentDetail>;

  /** GET /v1/library/types — library.py `list_types`. */
  listDocumentTypes(): Promise<DocumentType[]>;

  /**
   * GET /v1/weather/when-to-work — weather.py `when_to_work`, over
   * insight/planning.py. Needs BOTH capacity:read and weather:read, and the
   * platform requires both deliberately: the answer is only as trustworthy as
   * its weaker half, and somebody holding one key would otherwise be handed a
   * joint verdict built on data they may not see.
   */
  whenToWork(query: PlanQuery): Promise<PlanAnswer>;

  /** GET /v1/liability/curve — liability.py `curve`. Requires insight:read. */
  liabilityCurve(siteId: string, fiscalYear: number): Promise<LiabilityCurve>;

  /** GET /v1/liability/register — liability.py `register`. Requires insight:read. */
  liabilityRegister(siteId: string): Promise<LiabilityRegister>;

  /* ---- Asset Management — routers/asset_management.py ---------------------------- */

  /** GET /v1/asset-management/register — zone tree, types and the filtered register. site:read. */
  amRegister(siteId: string, filter?: AmFilter): Promise<AmRegister>;
  /** GET /v1/asset-management/assets/{id} — everything known about one asset. */
  amAsset(assetId: string): Promise<AmAssetDetail>;
  /** PATCH /v1/asset-management/assets/{id} — audited edit. asset:write. */
  amEditAsset(assetId: string, patch: AmPatch): Promise<{ changed: string[] }>;
  /* ── build 2: certificates ── */
  /**
   * A new certificate in the library (domain/certificates.ts): POST /v1/library/documents, then its first
   * issue (POST …/revisions, with the file), then — when `issue.issueNow` — POST /v1/library/revisions/{id}/transition
   * to issued. Each step carries `${key}:<step>` as its Idempotency-Key, so a retry returns the same document.
   * document:write; issuing needs document:approve. A type that requires review is recorded and left waiting.
   */
  recordCertificate(input: NewCertificateInput, key?: string): Promise<CertificateWrite>;
  /** A renewal: a new issue of a certificate the library holds, put in force at once when `issueNow`. */
  renewCertificate(documentId: string, input: CertificateIssueInput, key?: string): Promise<CertificateWrite>;
  /** Put a recorded issue in force (a renewal waiting for an approver). document:approve. */
  issueCertificate(revisionId: string): Promise<RevisionTransitionResult>;
  /** POST /v1/library/revisions/{id}/file — the paper for an issue recorded without it; once only (409 after). */
  attachRevisionFile(revisionId: string, file: File, key?: string): Promise<AttachedFile>;
  /** GET /v1/library/revisions/{id}/content — the file of one issue. */
  downloadRevision(revisionId: string): Promise<Blob>;
  /** POST /v1/asset-management/import — lifecycle fields for many assets, by tag. asset:write. */
  amImport(siteId: string, rows: AmImportRow[]): Promise<AmImportResult>;
  /** GET /v1/asset-management/policies — design life and replacement cost per asset type. */
  amPolicies(): Promise<AmPolicy[]>;
  /** PUT /v1/asset-management/policies/{type}. asset:write. */
  amSetPolicy(assetType: string, policy: Omit<AmPolicy, "assetType">): Promise<AmPolicy>;
  /** GET /v1/asset-management/condition — criticality × health, the risks. */
  amCondition(siteId: string): Promise<AmCondition>;
  /** POST /v1/asset-management/health/refresh. asset:write. */
  amRefreshHealth(siteId: string): Promise<{ assessed: number; changed: number }>;
  /** GET /v1/asset-management/maintenance — plans, compliance, MTBF/MTTR. */
  amMaintenance(siteId: string): Promise<AmMaintenance>;
  /** POST /v1/asset-management/plans. asset:write. */
  amCreatePlan(input: AmPlanInput): Promise<AmPlan>;
  /** PATCH /v1/asset-management/plans/{id}. asset:write. */
  amEditPlan(
    planId: string,
    patch: { active?: boolean; intervalDays?: number; nextDueOn?: string | null; title?: string },
  ): Promise<void>;
  /** POST /v1/asset-management/plans/apply — one plan on every asset of a type. asset:write. */
  amApplyPlan(siteId: string, assetType: string, plan: Omit<AmPlanInput, "assetId">): Promise<{ created: number }>;
  /** POST /v1/asset-management/plans/generate — raise what is due. work_order:write. */
  amGeneratePlans(siteId: string): Promise<{ created: { planId: string; workOrder: string; asset: string }[] }>;
  /** GET /v1/asset-management/lifecycle — age, book value, renewal forecast, warranties. */
  amLifecycle(siteId: string): Promise<AmLifecycle>;
  /** GET /v1/asset-management/types — installed asset types with their attribute schemas. */
  amTypes(): Promise<AmTypeDef[]>;
  /** POST /v1/asset-management/assets — register a new asset. asset:write. */
  amCreateAsset(siteId: string, input: AmCreateInput): Promise<{ id: string; tag: string }>;
  /** GET /v1/asset-management/assets/{id}/history — changes, work, defects and notes. */
  amHistory(assetId: string): Promise<AmHistoryEvent[]>;
  /** GET /v1/asset-management/assets/{id}/notes. */
  amNotes(assetId: string): Promise<AmNote[]>;
  /** POST /v1/asset-management/assets/{id}/notes — append-only. note:write. */
  amAddNote(assetId: string, body: string, category: NoteCategory): Promise<void>;

  /* ── build 2: closures ── */
  /**
   * POST /v1/closures — closure:write. Close a runway or taxiway under its
   * NOTAM, now or from a later start. The platform stamps who and when; the
   * same idempotency key never records it twice.
   */
  declareClosure(input: DeclareClosureInput, idempotencyKey?: string): Promise<DeclaredClosure>;
  /** POST /v1/closures/{id}/end — closure:write. Reopen it; the platform stamps who lifted it. */
  endClosure(id: string, endedAt?: string, idempotencyKey?: string): Promise<EndedClosure>;

  /* ---- HR: the workforce, its cost under Saudi law, and the work priced in its time ---- */
  /** GET /v1/hr/people. hr:read. */
  hrPeople(siteId: string): Promise<HrPeople>;
  /** POST /v1/hr/people. hr:write. */
  hrAddPerson(siteId: string, input: HrEmployeeInput): Promise<{ id: string; employeeNo: string }>;
  /** PATCH /v1/hr/people/{id}. hr:write. */
  hrEditPerson(employeeId: string, patch: HrEmployeePatch): Promise<{ changed: string[] }>;
  /** GET /v1/hr/rules. hr:read. */
  hrRules(): Promise<HrRulesView>;
  /** PUT /v1/hr/rules — the whole restated set; {} returns to the defaults. hr:write. */
  hrSetRules(restated: HrRulesPatch): Promise<HrRulesView>;
  /** GET /v1/hr/cost. hr:read. */
  hrCost(siteId: string): Promise<HrCost>;
  /** PUT /v1/hr/overtime — one person, one month; 0 hours removes it. hr:write. */
  hrSetOvertime(employeeId: string, month: string, hours: number): Promise<void>;
  /** GET /v1/hr/labour. hr:read. */
  hrLabour(siteId: string): Promise<HrLabour>;
  /** GET /v1/hr/saudization. hr:read. */
  hrSaudization(siteId: string): Promise<HrSaudization>;
  /** GET /v1/hr/people/{id} — the staff file. hr:read; pay needs hr:pay, reviews hr:confidential. */
  hrFile(employeeId: string): Promise<HrStaffFile>;
  /** PUT /v1/hr/people/{id}/photo. hr:write. */
  hrSetPhoto(employeeId: string, dataUrl: string): Promise<void>;
  /** GET /v1/hr/records — every record the reader may see. */
  hrRecords(
    siteId: string,
    filter?: { kind?: string; status?: string },
  ): Promise<{ records: HrRecord[]; confidentialHidden: boolean }>;
  /** POST /v1/hr/records. hr:write (+ hr:confidential for reviews and discipline). */
  hrAddRecord(input: HrRecordInput): Promise<HrRecord>;
  /** POST /v1/hr/records/{id}/decision. hr:approve for leave and letters. */
  hrDecide(recordId: string, status: RecordStatus, note?: string | null): Promise<HrRecord>;
  /** GET /v1/hr/attendance — everybody for one day. */
  hrAttendanceDay(siteId: string, day: string): Promise<HrAttendanceDay>;
  /** GET /v1/hr/attendance/range. */
  hrAttendanceRange(siteId: string, from: string, to: string, employeeId?: string): Promise<HrAttendanceRow[]>;
  /** PUT /v1/hr/attendance — one day, one status, for the people chosen. hr:write. */
  hrSetAttendance(input: HrAttendanceInput): Promise<{ recorded: number }>;
  /** GET /v1/hr/access — the HR levels and who holds them. */
  hrAccess(): Promise<HrAccessView>;
  /** PUT /v1/hr/access/{principal} — one HR level or none. hr:admin. */
  hrSetAccess(principalId: string, role: string | null): Promise<void>;
  /** GET /v1/hr/me — my record, today's check-ins, where I may check in, my requests. hr:clock. */
  hrMe(): Promise<HrMe>;
  /** POST /v1/hr/me/clock — check in or out by location. hr:clock. */
  hrClockMe(
    input: HrClockInput,
  ): Promise<{ at?: string; place?: string; distanceM?: number | null; duplicate?: boolean }>;
  /** POST /v1/hr/me/requests — ask HR for something. hr:clock. */
  hrSelfRequest(input: HrSelfRequest): Promise<HrRecord>;
  /** GET /v1/hr/geofences. */
  hrGeofences(siteId: string): Promise<HrGeofence[]>;
  /** POST /v1/hr/geofences. hr:admin. */
  hrAddGeofence(siteId: string, f: Omit<HrGeofence, "id">): Promise<HrGeofence>;
  /** PATCH /v1/hr/geofences/{id}. hr:admin. */
  hrEditGeofence(id: string, patch: Partial<HrGeofence>): Promise<void>;
  /** GET /v1/hr/clock-events — one day. */
  hrClockEvents(siteId: string, day: string, employeeId?: string): Promise<HrClockEvent[]>;
  /** POST /v1/hr/clock/manual — HR records one by hand, with why. hr:write. */
  hrClockManual(employeeId: string, direction: "in" | "out", at: string, note: string): Promise<void>;
  /** GET /v1/hr/notifications — the outbox. */
  hrNotifications(
    siteId: string,
    filter?: { employeeId?: string; status?: string },
  ): Promise<{ notifications: HrNotification[]; channels: HrChannels }>;
  /** POST /v1/hr/notifications/send. hr:admin. */
  hrSendNow(): Promise<{ sent: number; failed: number; not_configured: number }>;
  /** POST /v1/hr/notifications/{id}/retry. hr:write. */
  hrRetryNotification(id: string): Promise<void>;
  /** POST /v1/hr/people/import — many employees from a spreadsheet; dryRun checks and saves nothing. hr:write. */
  hrImport(
    siteId: string,
    rows: { row: number; data: Record<string, unknown>; loginEmail: string | null; leaveBalance: number | null }[],
    opts: { dryRun: boolean; updateExisting: boolean },
  ): Promise<ImportResponse>;

  /** GET /v1/capacity/closure-cost — capacity.py `closure_cost`. Requires capacity:read. */
  closureCost(q: ClosureQuery): Promise<ClosureCost>;

  /** GET /v1/capacity/critical — capacity.py `critical`. Requires capacity:read. */
  criticalPositions(siteId: string, from: string, to: string): Promise<CriticalReport>;

  /** GET /v1/drift/checks — drift.py `checks`. Requires drift:read. */
  driftChecks(siteId: string): Promise<DriftCheck[]>;

  /** GET /v1/drift/due — drift.py `due`. Requires drift:read. */
  driftSchedule(siteId: string): Promise<DriftSchedule>;

  /** GET /v1/reliability/populations — reliability.py. Requires reliability:read. */
  populations(siteId: string): Promise<Population[]>;

  /** GET /v1/reliability/fits — reliability.py. Requires reliability:read. */
  fits(siteId: string, population: string): Promise<Fit[]>;

  /** GET /v1/ledger/facts — ledger.py. Requires ledger:read. */
  /* ── replacing the system that is already there ───────────────────────
     The platform's migration subsystem: a screen contract per legacy screen,
     shadow running, an agreement rate, triage on every difference, and a
     cutover guard that refuses on evidence. See domain/migration.ts. */

  /** GET /v1/migrate/screens — migrate.py `list_screens` over cutover.status. Requires migrate:read. */
  migrationStatus(): Promise<MigrationStatus>;

  /** GET /v1/migrate/screens/{key} — the contract, its evidence, what blocks a cutover, and its transitions. */
  migrationScreen(key: string): Promise<ScreenDetail>;

  /** GET /v1/migrate/screens/{key}/differences — what the two systems disagreed about. */
  migrationDifferences(key: string, untriagedOnly: boolean): Promise<Difference[]>;

  /* ── build 2: inspections ── */
  /**
   * POST /v1/inspections with the round inline — work.py `start_inspection` → rounds.py
   * `record_round`. Started, answered, completed and its work raised in one transaction.
   * Requires inspection:write. Idempotent on the round's client id and on `idempotencyKey`.
   */
  recordInspectionRound(input: InspectionRoundInput, idempotencyKey?: string): Promise<InspectionRoundResult>;

  /**
   * GET /v1/inspections/rounds?site_id&template — completed rounds of these checklists, with
   * every answer and the work each raised. Requires inspection:read.
   */
  listInspectionRounds(query: InspectionRoundQuery): Promise<InspectionRound[]>;

  /** POST /v1/migrate/observations/{id}/triage — which side was wrong. Requires migrate:triage. */
  triageObservation(id: number, triage: Triage, note?: string): Promise<void>;

  /**
   * POST /v1/migrate/screens/{key}/mode — off, shadow or live. Requires migrate:cutover.
   * Going live is guarded: where the guard refuses, `blocking` says why and
   * nothing moved. `force` records an override rather than hiding one.
   */
  setScreenMode(key: string, mode: ScreenMode, reason: string, force?: boolean): Promise<ModeChange>;

  /**
   * GET /v1/field/positions — where the crews are, from the phones.
   *
   * The latest position per person, not a track, each with its age and
   * whether the platform considers it stale. A stale one is the last known
   * and says nothing about where the crew is now.
   */
  fieldPositions(siteId: string): Promise<FieldPosition[]>;

  /* ── administering the platform ───────────────────────────────────────
     Everything here needs an admin key and refuses without one. A screen
     calls these to show what is, and to change who may do what; none of them
     grants anything the API would not already allow the caller. */

  /** GET /v1/admin/setup — what this deployment still needs, computed from its own database. */
  adminSetup(): Promise<SetupState>;

  /** GET /v1/admin/principals — everybody in this tenant, with the roles they hold. */
  adminPeople(): Promise<Person[]>;

  /** POST /v1/admin/principals — add somebody, with their roles. No email is sent; there is no inbox. */
  adminAddPerson(input: NewPerson): Promise<AddedPerson>;

  /** POST /v1/admin/principals/{id}/disabled — stop somebody signing in, keeping their record. */
  adminSetDisabled(principalId: string, disabled: boolean): Promise<void>;

  /** GET /v1/admin/roles — the roles, their permissions and the level each grants its holders. */
  adminRoles(): Promise<RoleRow[]>;

  /* ── build 2: friction ── */

  /** GET /v1/readings/surveys — the site's friction surveys, newest first, each with its runs (insight:read). */
  listFrictionSurveys(siteId: string): Promise<import("../domain/friction").FrictionSurvey[]>;

  /**
   * POST /v1/readings/surveys — a whole survey in one go (reading:write).
   * 409 when it is already held; the key makes a retry give the first answer back.
   */
  importFrictionSurvey(
    siteId: string,
    input: import("../domain/friction").NewFrictionSurvey,
    idempotencyKey?: string,
  ): Promise<import("../domain/friction").FrictionSurveyAck>;

  /** POST /v1/work-orders/record-completed — finished work, completed on the day it was done (work_order:write). */
  recordCompletedWork(input: import("../domain/friction").CompletedWorkInput): Promise<import("../domain/friction").CompletedWorkAck>;

  /** POST /v1/work-orders/{id}/record — complete or correct that record; the status does not move. */
  amendCompletedWork(
    id: string,
    patch: import("../domain/friction").CompletedWorkPatch,
  ): Promise<import("../domain/friction").CompletedWorkAmended>;

  /** GET /v1/insight/model-params/{model}?site_id — the site's own figures for a model, and who saved them. */
  getModelParams(siteId: string, model: string): Promise<import("../domain/friction").ModelParams>;

  /** PUT /v1/insight/model-params/{model} — save them as a set (insight:run). */
  saveModelParams(
    siteId: string,
    model: string,
    input: import("../domain/friction").ModelParamsInput,
  ): Promise<import("../domain/friction").ModelParams>;

  /** PUT /v1/admin/roles/{key}/access-level — what holders are offered. Never what they are permitted. */
  adminSetRoleLevel(key: string, level: AccessLevel): Promise<void>;

  /** POST /v1/admin/role-assignments — give somebody a role, optionally at one site only. */
  adminAssignRole(principal: string, role: string, siteId?: string): Promise<void>;

  /** DELETE /v1/admin/role-assignments — take one back. */
  adminUnassignRole(principal: string, role: string, siteId?: string): Promise<void>;

  /** GET /v1/admin/auth/status — how this deployment authenticates, and whether the development header is on. */
  adminAuthStatus(): Promise<AuthStatus>;

  /**
   * POST /v1/admin/invitations — add somebody by email. The provider creates
   * their sign-in account (or finds it) and emails the set-password link; the
   * principal is bound to it with the role given. 503 means the API's service
   * account may not manage sign-in accounts yet.
   */
  adminInvite(input: Invitation): Promise<Invited>;

  /** PUT /v1/admin/identity-providers/{id} — switch it on or off, or set the role first sign-ins get. */
  adminUpdateProvider(id: string, change: ProviderChange): Promise<void>;

  /** POST /v1/admin/identity-providers/{id}/refresh-keys — fetch the provider's signing keys now. */
  adminRefreshProviderKeys(id: string): Promise<number>;

  ledgerFacts(siteId: string): Promise<FactSummary[]>;

  /** GET /v1/ledger/between — ledger.py, over ledger/divergence.py. Requires ledger:read. */
  divergence(q: DivergenceQuery): Promise<Divergence>;

  /** GET /v1/weather/can-work — weather.py `can_work`. Requires weather:read. */
  canWork(siteId: string, category: string, at: string): Promise<CanWork>;

  /**
   * GET /v1/programme — liability.py `programme_endpoint`, over
   * insight/programme.py. Needs insight:read, capacity:read AND weather:read:
   * a programme is only as trustworthy as its weakest input.
   */
  programme(siteId: string, fiscalYear: number, envelope?: number): Promise<Programme>;

  /**
   * GET /v1/counterfactual — liability.py `counterfactual_endpoint`, over
   * insight/counterfactual.py. Same three keys as the programme it varies: a
   * comparison is only as trustworthy as the baseline underneath it.
   */
  counterfactual(siteId: string, fiscalYear: number, crews?: number): Promise<Study>;

  /**
   * GET /v1/provenance/candidate/{id} — liability.py `provenance_endpoint`,
   * over insight/provenance.py. Requires insight:read.
   */
  provenance(siteId: string, candidateId: string): Promise<Chain>;

  /** POST /v1/library/documents — library.py `create_document`. Requires document:write; 422 on an unknown type. */
  createDocument(input: NewDocumentInput): Promise<NewDocumentResult>;

  /**
   * POST /v1/library/documents/{id}/revisions (multipart) — library.py `add_revision`. Drafts a revision,
   * optionally with the file. Requires document:write; 422 when the document is withdrawn, the type
   * declares an expiry and none was given, or the content type is not accepted.
   */
  addRevision(documentId: string, input: NewRevisionInput): Promise<NewRevisionResult>;

  /**
   * POST /v1/library/revisions/{id}/transition — library.py `transition_revision`. `approved` and `issued`
   * require document:approve, the rest document:write; 409 carries the platform's reason verbatim.
   */
  transitionRevision(revisionId: string, input: RevisionTransitionInput): Promise<RevisionTransitionResult>;

  /** POST /v1/library/revisions/{id}/acknowledge — library.py `acknowledge`. Marks only the caller's own row. */
  acknowledgeRevision(revisionId: string): Promise<{ acknowledged: boolean }>;

  /** GET /v1/projects/vocabulary — projects.py `get_vocabulary`: the gate sets, disciplines and categories the packs declare. */
  getProjectVocabulary(): Promise<ProjectVocabulary>;

  /** GET /v1/projects?site_id&status&category&live_only — projects.py `list_projects`. */
  listProjects(query: ProjectQuery): Promise<ProjectRow[]>;

  /** GET /v1/projects/{id} — projects.py `get_project`, with its gates, inspections, links and hold points. */
  getProject(id: string): Promise<ProjectDetail>;

  /** GET /v1/projects/hold-points?site_id — projects.py `hold_points`: every unreleased hold point, and why each holds. */
  listHoldPoints(siteId: string): Promise<HoldPointReport>;

  /** GET /v1/projects/attention?site_id&within_days — projects.py `needs_attention`: nine separate lists, never a total. */
  getProjectAttention(siteId: string): Promise<AttentionReport>;

  /** GET /v1/projects/inspections/{id} — projects.py `get_inspection`: the sheet, and whether it is holding the work. */
  getProjectInspection(id: string): Promise<ProjectInspectionDetail>;

  /** POST /v1/projects — projects.py `create_project`. Requires project:write; the gate set must be one a pack declares. */
  createProject(siteId: string, input: CreateProjectInput): Promise<{ id: string; code: string }>;

  /** POST /v1/projects/{id} — projects.py `update_project`: descriptive and financial fields, audited. */
  updateProject(id: string, input: UpdateProjectInput): Promise<void>;

  /** POST /v1/projects/{id}/schedule — projects.py `set_schedule`. Moving an existing baseline needs a reason (409 otherwise). */
  setProjectSchedule(id: string, input: ScheduleInput): Promise<ScheduleResult>;

  /** GET /v1/projects/{id}/log — projects.py `project_log`: notes, updates and actions, newest first. */
  getProjectLog(id: string): Promise<ProjectLog>;

  /** POST /v1/projects/{id}/log — projects.py `add_log_entry`. Requires project:write. */
  addProjectLogEntry(id: string, input: NewLogEntryInput): Promise<void>;

  /** POST /v1/projects/log/{entry}/close — projects.py `close_action`: done, or cancelled with a note. */
  closeProjectAction(entryId: string, input: CloseActionInput): Promise<void>;

  /** POST /v1/projects/{id}/gates/{key}/submit — projects.py `submit_gate`. Requires project:write. */
  submitGate(projectId: string, gateKey: string, input: GateSubmitInput): Promise<GateSubmitResult>;

  /**
   * POST /v1/projects/{id}/gates/{key}/decide — projects.py `decide_gate`. Requires **project:gate**, which
   * is not project:write: approving a gate is a different act from updating the project it is on.
   */
  decideGate(projectId: string, gateKey: string, input: GateDecisionInput): Promise<GateDecisionResult>;

  /** POST /v1/projects/inspections/{id}/items — projects.py `record_items`. Requires project:write. */
  recordInspectionItems(inspectionId: string, results: ItemResultInput[]): Promise<InspectionWriteResult>;

  /** POST /v1/projects/inspections/{id}/result — projects.py `record_result`. This does NOT release a hold point. */
  recordInspectionResult(inspectionId: string, input: RecordResultInput): Promise<InspectionWriteResult>;

  /**
   * POST /v1/projects/inspections/{id}/release — projects.py `release_hold`. Requires
   * **project:release_hold**: letting the work continue is not recording what was found. 409 names the refusal.
   */
  releaseHoldPoint(inspectionId: string, note?: string): Promise<InspectionWriteResult>;

  /** GET /v1/notifications?site_id&unread_only — ops.py `list_notifications`. */
  listNotifications(siteId: string): Promise<Notification[]>;

  /**
   * POST /v1/assist/ask — assist/answer.py, over the question catalogue.
   *
   * The platform's own assistant, and the only route by which Sky is allowed
   * to state a figure about this estate. Every number it returns came from a
   * catalogued query run as the asking operator, with the rows attached; it
   * refuses what the catalogue does not cover rather than reaching for general
   * knowledge. Requires **assist:ask**, and is burst-limited per principal —
   * 429 carries `Retry-After`.
   *
   * Pass `sessionId` back from the previous reply to make follow-ups work.
   */
  askAssistant(query: AssistQuestion): Promise<AssistReply>;

  /**
   * What the deployment can compose wording with, if anything.
   *
   * Separate from `askAssistant`, and deliberately so: that answers from the
   * catalogue and touches the database; this only puts words to material the
   * caller already has on screen. An adapter with no deployment behind it
   * returns `configured: false`, which is not a failure — it is how the
   * console knows to fall back to a key of its own, or to the platform's own
   * readings, rather than calling and being refused.
   */
  skyModel(): Promise<SkyModel>;

  /**
   * Put words to material the caller already has. Requires **assist:ask**.
   *
   * Metered on the deployment twice over — calls per day and tokens per day —
   * because it spends the operator's money. 501 means no key is held, 502
   * means the provider could not be reached; the two are different and a
   * console should say which.
   */
  composeWithModel(brief: string, material: string, signal?: AbortSignal): Promise<ComposedText>;

  /**
   * Talk to Sky with the deployment's model choosing the lookups
   * (`POST /v1/assist/converse`). The model reaches the estate only through
   * the platform's own tools, run as this person; a work order comes back as
   * a draft, never raised. 501 means the deployment holds no conversational
   * model, and the console carries on with its own readings.
   */
  converseWithSky(input: SkyConverseInput, signal?: AbortSignal): Promise<SkyConverseReply>;

  /** POST /v1/assist/voice-log — what the browser's microphone did, kept in the server's log. */
  sendVoiceLog(lines: string[]): Promise<void>;

  /* ── build 4: approvals on the stores chain ── */

  /**
   * GET /v1/stores/approvals?site_id — the approval chain's requests, newest
   * first: the steps signed, the step each waits on, whether THIS person may
   * sign it and, when not, why in the platform's words; which steps they hold
   * and what awaits them (stores:read).
   */
  approvalsBoard(siteId: string): Promise<import("../domain/approvals").ApprovalsBoard>;

  /**
   * POST /v1/stores/requests with chain "approvals" — a request into the chain
   * (stores:write). Who raised it and when are the platform's stamps.
   */
  raiseApprovalRequest(
    siteId: string,
    input: import("../domain/approvals").NewApprovalRequest,
    idempotencyKey?: string,
  ): Promise<{ number: string; replayed?: boolean }>;

  /**
   * POST /v1/stores/requests/{number}/step — sign the request's NEXT step as
   * the person signed in. No role or step is sent: the platform decides which
   * step it is and whether this person holds it (approval:<step>).
   */
  signApprovalStep(
    siteId: string,
    number: string,
    decision: "approved" | "rejected",
    note?: string,
    idempotencyKey?: string,
  ): Promise<import("../domain/approvals").ApprovalStepAck>;

  /** POST /v1/stores/requests/{number}/order — the purchase order for an approved request (stores:approve). */
  orderApprovalRequest(
    siteId: string,
    number: string,
    supplier?: string,
    idempotencyKey?: string,
  ): Promise<{ number: string; replayed?: boolean }>;

  /* ── build 5: shared registers, row by row ── */

  /** GET /v1/registers/{key}/rows — the register's rows in the order first written, and whether this person may change them. */
  listRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    opts?: { includeDeleted?: boolean },
  ): Promise<import("../domain/registerRows").RegisterRows>;

  /**
   * POST /v1/registers/{key}/changes — every change or none. A change from a
   * version somebody has moved on since throws `RowConflict`, carrying their row.
   */
  changeRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    ops: import("../domain/registerRows").RowOp[],
  ): Promise<import("../domain/registerRows").RegisterRow[]>;

  /** POST /v1/registers/{key}/import — a register kept as a note brought across, once, into an empty register. */
  importRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    rows: { id: string; data: Record<string, unknown> }[],
    noteId?: string,
  ): Promise<{ imported: number }>;
}

export interface SkyConverseInput {
  message: string;
  siteId: string;
  sessionId?: string;
  /** The person's own date and time, as they would say it — so she does not say good morning at night. */
  localTime?: string;
  history: { role: "user" | "assistant"; content: string }[];
  /** The screen the person is on, as the console knows it. */
  screen?: { route: string; title: string; summary: string[]; material?: string };
  /** The screens this person may open, for the model to offer and open. */
  screens: { route: string; title: string }[];
}

export interface SkyConverseReply {
  text: string;
  model: string;
  sessionId?: string;
  /** Every lookup that ran, as the query log records it. */
  steps: { intent: string; rowCount: number }[];
  /** A work order the model prepared. Nothing is raised until the person confirms. */
  draft?: {
    title: string;
    priority: number;
    kind: string;
    description?: string;
    assetTag?: string;
    zoneCode?: string;
    slaHours?: number;
  };
  /** A screen the model asked the console to open. */
  navigate?: string;
  /** True when the model's wording carried a figure no tool returned, and the platform's stood instead. */
  withheld: boolean;
  /** True when part of the reply is the model's general knowledge, labelled so — not this airport's records. */
  general?: boolean;
}

export interface SkyModel {
  configured: boolean;
  /** The model the deployment holds, e.g. "claude-sonnet-5". Null when none is. */
  model: string | null;
  provider: string | null;
  /** The deployment's own sentence about what it does and does not send. */
  note: string;
}

export interface ComposedText {
  text: string;
  model: string;
  /** What the call cost, and what is left of today's ceiling. */
  tokens: { input: number; output: number; remainingToday: number | null };
}

export interface Principal {
  /** Display name. */
  name: string;
  /** Token subject or API-key principal. */
  subject?: string;
  method?: string;
  groups: string[];
  /** Effective permissions (scopes ∩ role). Used to disable, never to enforce. */
  permissions: string[];
  /**
   * What this person is OFFERED, issued by the platform from the roles they
   * hold: operations, engineering, executive or master.
   *
   * It decides which screens a client puts in front of them and what the
   * assistant will discuss. It grants nothing — every endpoint still checks
   * its own permission key — so a level set too high shows a screen whose
   * data the API then refuses, which is noisy and safe.
   *
   * Absent from a platform older than migration 0057; the client then falls
   * back to reading the groups, and says that is what it is doing.
   */
  accessLevel?: "operations" | "engineering" | "executive" | "master";
}

export interface Bootstrap {
  tenant?: { name?: string; slug?: string };
  principal: Principal;
  sites: Site[];
  packs: { key: string; version: string }[];
  /** True when the platform is accepting the development headers. */
  developmentAuth: boolean;
  /** Time the bootstrap was received; the platform does not send its clock. */
  receivedAt: string;
}

export interface WorkOrderQuery {
  siteId: string;
  /** The platform filters one status server-side; several are filtered client-side. */
  status?: WorkOrderStatus;
}

export interface InspectionQuery {
  siteId: string;
  status?: InspectionStatus;
  template?: string;
}

export interface DocumentQuery {
  siteId: string;
  /** applicable (default): tenant-wide documents included; exclusive: tagged with this site only. */
  siteScope?: "applicable" | "exclusive";
  type?: string;
  /** The type's category, e.g. "certificate": every type the packs file under it. */
  category?: string;
  status?: DocumentStatus;
  tag?: string;
  q?: string;
}

export interface WorkOrderPage {
  items: WorkOrder[];
  /** The platform returns the first `limit` by priority and says so. */
  truncated: boolean;
  note?: string;
}

export interface Notification {
  id: string;
  at: string;
  severity?: number;
  role?: string;
  title: string;
  body?: string;
  ruleKey?: string;
  subjectKind?: string;
  subjectId?: string;
  read: boolean;
}

/** Error raised by any adapter. `status` is the HTTP status when there was one. */
export class DataSourceError extends Error {
  readonly status?: number;
  readonly detail?: unknown;
  constructor(message: string, opts: { status?: number; detail?: unknown; cause?: unknown } = {}) {
    super(message, { cause: opts.cause });
    this.name = "DataSourceError";
    this.status = opts.status;
    this.detail = opts.detail;
  }
}

/** Permission keys as the platform's routers spell them (`ident.require(...)`). */
export const PERMISSION = {
  siteRead: "site:read",
  siteWrite: "site:write",
  /* Asset Management edits, policies and maintenance plans. */
  assetWrite: "asset:write",
  workOrdersRead: "work_order:read",
  workOrdersWrite: "work_order:write",
  workOrdersVerify: "work_order:verify",
  insightRead: "insight:read",
  inspectionsRead: "inspection:read",
  geoRead: "geo:read",
  documentsRead: "document:read",
  documentsWrite: "document:write",
  documentsApprove: "document:approve",
  ruleVerify: "rule:verify",
  driftRead: "drift:read",
  reliabilityRead: "reliability:read",
  ledgerRead: "ledger:read",
  /* The planning join needs both halves; see `whenToWork` above. */
  capacityRead: "capacity:read",
  weatherRead: "weather:read",
  /*
   * Projects carry three keys, not one, and the separation is the pack's whole
   * point (`projects.py`): `project:write` records what was found, `project:gate`
   * decides a stage gate, and `project:release_hold` lets stopped work continue.
   * A screen that disabled all three on one key would be describing a system
   * that has gates only as a form.
   */
  projectsRead: "project:read",
  projectsWrite: "project:write",
  projectsGate: "project:gate",
  projectsReleaseHold: "project:release_hold",
  /* Append-only notes about the estate — engineers, supervisors, inspectors, technicians. */
  notesWrite: "note:write",
  testsRead: "test:read",
  testsWrite: "test:write",
  evidenceWrite: "evidence:write",
  /* Pay is personal data: its own keys, not site:read. */
  hrRead: "hr:read",
  hrWrite: "hr:write",
  hrPay: "hr:pay",
  hrConfidential: "hr:confidential",
  hrApprove: "hr:approve",
  hrAdmin: "hr:admin",
  /* Store & Warehouse: read, move stock and keep the catalogue, approve and order. */
  storesRead: "stores:read",
  storesWrite: "stores:write",
  storesApprove: "stores:approve",
  /* The approval chain (0090): one key per step, asked of the step a request waits on. */
  approvalEngineering: "approval:engineering",
  approvalContracts: "approval:contracts",
  approvalPurchasing: "approval:purchasing",
  approvalFinance: "approval:finance",
  approvalCoo: "approval:coo",
  approvalCeo: "approval:ceo",
  /* The finance system: seeing its figures, importing a file, and connecting SAP — an administrator's. */
  financeRead: "finance:read",
  financeImport: "finance:import",
  financeConnect: "finance:connect",
  /*
   * Safety (SMS): reading the register, acting on hazards, filing a report,
   * reading reports, seeing a protected reporter, and closing or accepting a
   * risk — kept apart on purpose (0019): a technician files, the safety office reads.
   */
  safetyRead: "safety:read",
  safetyWrite: "safety:write",
  safetyReport: "safety:report",
  safetyReportRead: "safety:report_read",
  safetyReportIdentity: "safety:report_identity",
  safetyClose: "safety:close",
  /* Security incidents; restricted ones need their own clearance to raise or read. */
  securityRead: "security:read",
  securityIncidentWrite: "security:incident_write",
  securityReadRestricted: "security:read_restricted",
  /* Closing a runway or taxiway, and reading the closure register. */
  closureRead: "closure:read",
  closureWrite: "closure:write",
  /* Measurements: friction surveys and manual readings. */
  readingWrite: "reading:write",
  inspectionsWrite: "inspection:write",
  /* build 6B: moving a KPI target, and setting funding envelopes and unit rates. */
  kpiTarget: "kpi:target",
  insightWrite: "insight:write",
} as const;

export function hasPermission(p: Principal | undefined, key: string): boolean {
  if (!p) return false;
  return p.permissions.includes("*") || p.permissions.includes(key);
}

/**
 * What a planning question needs. Every field is required because the platform
 * refuses a search it cannot bound — an unbounded range is one database round
 * trip per sample point and takes the database down rather than answering.
 */
export interface PlanQuery {
  siteId: string;
  positionCode: string;
  category: string;
  hoursNeeded: number;
  searchFrom: string;
  searchTo: string;
  stepMinutes?: number;
}

/** A closure question: which position, and over what window. */
export interface ClosureQuery {
  siteId: string;
  positionCode: string;
  from: string;
  to: string;
}

/** A disagreement question: which fact, and between whom. */
export interface DivergenceQuery {
  siteId: string;
  factKey: string;
  partyA: string;
  partyB: string;
}
